import { Tabs, Tab } from '@mui/material';
import {
  GlobalPermission,
  SystemPermission_Permission,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { useTranslation } from 'react-i18next';
import { useLocation, matchPath, Link } from 'react-router';
import {
  DashboardIcon,
  DownloadIcon,
  ListIcon,
  MoneyIcon,
  NoteIcon,
  SystemIcon,
  UsersIcon,
  PdfIcon,
  AnalyseIcon,
  ChartIcon,
  AdminPageIcon,
} from '~/shared/components/Icons/Icons';
import {
  useHasGlobalPermission,
  useHasPermissionForSystem,
} from '~/shared/hooks/auth-hooks';
import { useRequiredSystem } from '~/shared/hooks/system-hooks';
import { useOptionalSimulation } from '~/shared/hooks/useSimulation';
import { useSimulationInfo } from '~/shared/hooks/useSimulationInfo';
import {
  createRoutes,
  createDashboardRoute,
  createSystemRoute,
  createDownloadsRoute,
  createSystemUsersRoute,
  createSystemNotesRoute,
  createSystemAdminRoute,
  createSystemDetailsRoute,
  createSystemBomRoute,
  createSystemAdviceReportRoute,
  createSystemSimulationsRoute,
  createDashboardSimulationRoute,
  createSystemSimulationRoute,
  createSimulationMetricsRoute,
  createMetricsRoute,
} from '~/shared/models/create-routes';
import { DefaultChartMetricsModel } from '~/shared/models/metrics/DefaultChartMetricsModel';

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

function useAvailableTabs() {
  const system = useRequiredSystem();
  const { t } = useTranslation();
  const isSystemInProduction =
    system.state === 'PRODUCTION' ||
    system.state === 'PRODUCTION_EDIT' ||
    system.state === 'OUT_OF_PRODUCTION';

  const isInAdviceOrConfiguration =
    system.state === 'ADVICE' || system.state === 'CONFIGURATION';

  const isInAdviceState = system.state === 'ADVICE';

  const canAccessUserManagement = useHasPermissionForSystem(
    SystemPermission_Permission.USER_MANAGEMENT,
    system.id,
  );

  const canSeeNotes = useHasPermissionForSystem(
    SystemPermission_Permission.USER_MANAGEMENT,
    system.id,
  );

  const canSeeDetails = useHasPermissionForSystem(
    SystemPermission_Permission.DESIGN,
    system.id,
  );

  const canSeeReportTab =
    useHasGlobalPermission(GlobalPermission.WAVIN_ONLY) && isInAdviceState;

  const canSeeSimulationsTab =
    useHasGlobalPermission(GlobalPermission.WAVIN_ONLY) && isInAdviceState;

  const canSeeBom =
    useHasGlobalPermission(GlobalPermission.WAVIN_ONLY) &&
    isInAdviceOrConfiguration;

  const canSeeAdminTab = useHasPermissionForSystem(
    SystemPermission_Permission.COMMISSIONING,
    system.id,
  );

  const { simulationId, isSimulation } = useSimulationInfo();
  const simulation = useOptionalSimulation();

  const defaultMetricsToDisplay =
    DefaultChartMetricsModel.getDefaultMetricsToDisplay({
      system,
      simulationType: simulation?.type,
    });

  if (isSimulation) {
    return [
      {
        value: createRoutes.SystemSimulation,
        label: t('navigation.system'),
        to: createSystemSimulationRoute(system.id, simulationId),
        icon: <SystemIcon />,
      },
      {
        value: createRoutes.DashboardSimulation,
        label: t('navigation.dashboard'),
        to: createDashboardSimulationRoute(system.id, simulationId),
        icon: <DashboardIcon />,
      },
      {
        value: createRoutes.SimulationMetrics,
        label: t('navigation.charts'),
        to: createSimulationMetricsRoute({
          systemId: system.id,
          simulationId,
          metrics: defaultMetricsToDisplay,
          range: {
            start: simulation.timeFromDate,
            end: simulation.timeToDate,
          },
        }),
        icon: <ChartIcon />,
      },
    ];
  }

  const routes = [
    {
      value: createRoutes.System,
      label: t('navigation.system'),
      to: createSystemRoute(system.id),
      icon: <SystemIcon />,
    },
  ];

  if (isSystemInProduction) {
    routes.push(
      {
        value: createRoutes.Dashboard,
        label: t('navigation.dashboard'),
        to: createDashboardRoute(system.id),
        icon: <DashboardIcon />,
      },
      {
        value: createRoutes.Downloads,
        label: t('navigation.downloads'),
        to: createDownloadsRoute(system.id),
        icon: <DownloadIcon />,
      },
      {
        value: createRoutes.Metrics,
        label: t('navigation.charts'),
        to: createMetricsRoute({
          systemId: system.id,
          metrics: defaultMetricsToDisplay,
        }),
        icon: <ChartIcon />,
      },
    );
  }

  if (canAccessUserManagement) {
    routes.push({
      value: createRoutes.SystemUsers,
      label: t('navigation.users'),
      to: createSystemUsersRoute(system.id),
      icon: <UsersIcon />,
    });
  }

  if (!isInAdviceState && canSeeNotes) {
    routes.push({
      value: createRoutes.SystemNotes,
      label: t('navigation.systemNotes'),
      to: createSystemNotesRoute(system.id),
      icon: <NoteIcon />,
    });
  }

  if (canSeeDetails) {
    routes.push({
      value: createRoutes.SystemDetails,
      label: t('navigation.details'),
      to: createSystemDetailsRoute(system.id),
      icon: <ListIcon />,
    });
  }

  if (canSeeBom) {
    routes.push({
      value: createRoutes.BOM,
      label: t('navigation.billOfMaterials'),
      to: createSystemBomRoute(system.id),
      icon: <MoneyIcon />,
    });
  }

  if (canSeeReportTab) {
    routes.push({
      value: createRoutes.AdviceReport,
      label: t('navigation.adviceReport'),
      to: createSystemAdviceReportRoute(system.id),
      icon: <PdfIcon />,
    });
  }

  if (canSeeSimulationsTab) {
    routes.push({
      value: createRoutes.SystemSimulations,
      label: t('navigation.simulations'),
      to: createSystemSimulationsRoute(system.id),
      icon: <AnalyseIcon />,
    });
  }

  if (canSeeAdminTab) {
    routes.push({
      value: createRoutes.SystemAdmin,
      label: t('navigation.admin'),
      to: createSystemAdminRoute(system.id),
      icon: <AdminPageIcon />,
    });
  }

  return routes;
}

export function SystemPageTabs() {
  const routes = useAvailableTabs();

  const routeMatch = useRouteMatch(routes.map(({ value }) => value));
  const currentTab = routeMatch?.pattern.path ?? routes[0].value;

  return (
    <Tabs
      value={currentTab}
      sx={{
        '& .MuiTabs-indicator': {
          backgroundColor: (theme) => theme.palette.secondary.main,
        },
        mb: 3,
        width: '100%',
        borderBottom: '1px solid',
        borderColor: (theme) => theme.palette.divider,
      }}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
    >
      {[...routes].map(({ value, label, to, icon }) => (
        <Tab
          key={value}
          label={label}
          value={value}
          to={to}
          component={Link}
          icon={icon}
          iconPosition="start"
          sx={{
            minHeight: 48,
            fontSize: (theme) => theme.typography['body-s'],
            lineHeight: 1,
            '&.Mui-selected': {
              color: (theme) => theme.palette.secondary.main,
            },
          }}
        />
      ))}
    </Tabs>
  );
}
